import { useEffect, useState } from 'react'

/**
 * A counter that increments each time `swap` becomes truthy — used as a React `key` suffix to
 * force a remount of a block's text editors on a column swap, without remounting on every
 * keystroke.
 *
 * `TwoColumnBlock`/`ThreeColumnBlock` key their `InlineEditableHtml` instances off this instead of
 * the field's own content: a plain content update from `InlineEditableHtml`'s own sync effect
 * leaves stale selection state pointed at the other column's old text, so a swap still needs a
 * real remount — but keying off content itself remounted (and dropped focus on) every keystroke,
 * since typing changes that same content (#23885).
 *
 * This tracks truthiness, not distinct truthy values — two different truthy `swap` values in a row
 * with no falsy value in between would only bump the counter once. Safe today because both callers
 * (`TwoColumnOptions`/`ThreeColumnOptions` set it, the block's own swap effect resets it to
 * `false`/`''` synchronously in the same effect run) always land a falsy value before `swap` can go
 * truthy again — a new caller that doesn't reset it the same way would silently lose a remount.
 */
export function useSwapVersion(swap: unknown): number {
  const [version, setVersion] = useState(0)

  useEffect(() => {
    if (swap) setVersion((v) => v + 1)
  }, [swap])

  return version
}
